import type React from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { cn } from '../../styles/lib/utils';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { Input } from '../Input';
import { Label } from '../Label';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { RadioBar, RadioBarItem } from '../RadioBar';
import { Text } from '../Text';
import { DayView, MonthView, QuarterView, YearView } from './components';
import type { DatePickerValue, DateView } from './utils';
import {
  datePickerValueKey,
  dateToISOString,
  formatDisplayText,
  getFirstDayOfMonth,
  getFirstDayOfQuarter,
  getFirstDayOfYear,
  getLastDayOfMonth,
  getLastDayOfQuarter,
  getLastDayOfYear,
} from './utils';

type DatePickerProps = {
  value?: DatePickerValue;
  onChange?: (value: DatePickerValue) => void;
  onBlur?: () => void;
  allowRange?: boolean;
  /** When true, stores dates in local timezone (e.g. "2026-04-09T04:00:00.000-05:00") instead of UTC */
  allowTimezone?: boolean;
  /** Earliest selectable date */
  minDate?: Date;
  disabled?: boolean;
  displayInput?: boolean;
  multiView?: boolean;
  placeholder?: string;
  /** When true, the date picker will be instantly selected when a date is picked. Only relevant when allowRange is false. */
  instantSelect?: boolean;
  /** Which view tab to show initially when no value is set. Only relevant when multiView is true. */
  defaultView?: DateView;
  /**
   * Which view tabs to offer when multiView is true (default: all four). A
   * single entry locks the picker to that view and hides the tab bar
   * (e.g. views={['Year']} for a year-only picker).
   */
  views?: DateView[];
  /** Inclusive year span offered by the Year view (default: current year plus the 11 following). */
  yearRange?: { start: number; end: number };
  /** When false, hides the clear (X) affordance so a set value can be replaced but not removed. */
  allowClear?: boolean;
  /** Trigger look: 'input' is the bordered field; 'text' reads as plain text (no border or trailing icon) until opened. */
  triggerVariant?: 'input' | 'text';
  label?: string;
  required?: boolean;
  className?: string;
  inputClassName?: string;
  id?: string;
  ref?: React.Ref<HTMLButtonElement>;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  /** Names the trigger when there is no visible `label`; the selected date is appended so it is still announced. */
  'aria-label'?: string;
  'data-testid'?: string;
};

function DatePicker({
  value = null,
  onChange,
  onBlur,
  allowRange = false,
  allowTimezone = false,
  minDate,
  disabled = false,
  displayInput = false,
  multiView = false,
  placeholder = 'Choose date',
  instantSelect = true,
  defaultView = 'Day',
  views = ['Day', 'Month', 'Quarter', 'Year'],
  yearRange,
  allowClear = true,
  triggerVariant = 'input',
  label,
  required = false,
  className,
  inputClassName,
  id: idProp,
  ref,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [internalView, setInternalView] = useState<DateView>(() => {
    if (!multiView) return 'Day';
    return views.includes(defaultView) ? defaultView : (views[0] ?? 'Day');
  });
  const [tempSelection, setTempSelection] = useState<DatePickerValue>(null);
  const [rangeStart, setRangeStart] = useState<Date | null>(null);

  // Initialize temp selection from value, keyed on content so a reference-only
  // value change (consumer rebuilding the object each render) can't reset it.
  const valueKey = datePickerValueKey(value);
  const valueRef = useRef(value);
  valueRef.current = value;
  // biome-ignore lint/correctness/useExhaustiveDependencies: valueKey is a trigger-only dependency — the effect reads the value through a ref and must fire on content change, not reference change
  useEffect(() => {
    const v = valueRef.current;
    if (v) {
      setTempSelection(v);
      setInternalView(v.view);
    } else {
      setTempSelection(null);
      setInternalView(multiView ? defaultView : 'Day');
    }
  }, [valueKey, multiView, defaultView]);

  const closePicker = () => {
    setOpen(false);
    onBlur?.();
  };

  const handleApply = () => {
    if (onChange && tempSelection) {
      onChange(tempSelection);
    }
    closePicker();
  };

  const handleCancel = () => {
    setTempSelection(value);
    setRangeStart(null);
    closePicker();
  };

  const handleDateSelect = (selectedDate: Date, view: DateView) => {
    if (allowRange && rangeStart === null) {
      // Start range selection - clear any existing range
      // If there's an existing range and clicked date is before its start, reset it
      if (tempSelection && 'startDate' in tempSelection) {
        const existingStart = new Date(tempSelection.startDate);
        if (selectedDate < existingStart) {
          setTempSelection(null);
          setRangeStart(selectedDate);
          return;
        }
      }
      setTempSelection(null);
      setRangeStart(selectedDate);
      return;
    }

    if (allowRange && rangeStart) {
      // If clicking a date before the range start, reset and start a new range
      if (selectedDate < rangeStart) {
        setTempSelection(null);
        setRangeStart(selectedDate);
        return;
      }

      // Complete range selection (selectedDate >= rangeStart, so range is in order)
      let start: Date = rangeStart;
      let end: Date = selectedDate;

      // For non-Day views, adjust to period boundaries
      if (view !== 'Day') {
        if (view === 'Month') {
          start = getFirstDayOfMonth(start.getFullYear(), start.getMonth() + 1);
          end = getLastDayOfMonth(end.getFullYear(), end.getMonth() + 1);
        } else if (view === 'Quarter') {
          const startQuarter = Math.floor(start.getMonth() / 3) + 1;
          const endQuarter = Math.floor(end.getMonth() / 3) + 1;
          start = getFirstDayOfQuarter(start.getFullYear(), startQuarter);
          end = getLastDayOfQuarter(end.getFullYear(), endQuarter);
        } else if (view === 'Year') {
          start = getFirstDayOfYear(start.getFullYear());
          end = getLastDayOfYear(end.getFullYear());
        }
      }

      setTempSelection({
        startDate: dateToISOString(start, allowTimezone),
        endDate: dateToISOString(end, allowTimezone),
        view,
      });
      setRangeStart(null);
    } else {
      // Single date selection
      let finalDate: Date = selectedDate;

      // For non-Day views, use last day of period
      if (view !== 'Day') {
        if (view === 'Month') {
          finalDate = getLastDayOfMonth(selectedDate.getFullYear(), selectedDate.getMonth() + 1);
        } else if (view === 'Quarter') {
          const quarter = Math.floor(selectedDate.getMonth() / 3) + 1;
          finalDate = getLastDayOfQuarter(selectedDate.getFullYear(), quarter);
        } else if (view === 'Year') {
          finalDate = getLastDayOfYear(selectedDate.getFullYear());
        }
      }

      const newValue = {
        date: dateToISOString(finalDate, allowTimezone),
        view,
      };
      setTempSelection(newValue);

      // If instantSelect is enabled, apply the value and close the popover
      if (instantSelect && !allowRange) {
        onChange?.(newValue);
        closePicker();
      }
    }
  };

  const displayText = formatDisplayText(tempSelection || value);
  const generatedId = useId();
  const inputId = idProp ?? generatedId;
  const requiredId = `${inputId}-required`;
  // A button role takes no aria-required, so the required state reaches assistive tech as a description.
  const describedBy = [ariaDescribedBy, required ? requiredId : undefined].filter(Boolean).join(' ') || undefined;

  const handleClear = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (onChange) {
      onChange(null);
    }
    setTempSelection(null);
    setRangeStart(null);
  };

  const handleClearKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClear(e);
    }
  };

  return (
    <div className={cn('flex flex-col gap-1.5 w-[258px]', className)}>
      {label && (
        <Label htmlFor={inputId} className="text-xs">
          {label}
          {required && (
            <span aria-hidden="true" className="text-destructive">
              *
            </span>
          )}
        </Label>
      )}
      {required && (
        <span id={requiredId} className="sr-only">
          Required
        </span>
      )}
      <Popover
        open={disabled ? false : open}
        onOpenChange={(newOpen) => {
          if (disabled && newOpen) return;
          if (!newOpen) {
            setTempSelection(value);
            setRangeStart(null);
            closePicker();
            return;
          }
          setOpen(true);
        }}
      >
        <PopoverTrigger asChild>
          <button
            ref={ref}
            type="button"
            id={inputId}
            aria-label={ariaLabel && !label ? [ariaLabel, displayText].filter(Boolean).join(', ') : undefined}
            aria-invalid={ariaInvalid}
            aria-describedby={describedBy}
            disabled={disabled}
            data-testid={testId}
            onBlur={() => {
              if (!open) onBlur?.();
            }}
            className={cn(
              'h-7 w-full min-w-0 rounded border px-2 py-1 text-xs leading-[1.4] text-left transition-[color,box-shadow,border-color] outline-none',
              triggerVariant === 'text'
                ? 'border-transparent bg-transparent cursor-pointer hover:bg-grey-10'
                : 'border-grey-30 bg-white',
              'focus-visible:border-focus',
              open && (triggerVariant === 'text' ? 'border-blue-50 bg-white hover:bg-white' : 'border-blue-50'),
              'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-grey-10',
              'flex items-center justify-between gap-2 relative',
              inputClassName,
            )}
          >
            <span className={cn('flex-1 text-left', !displayText && 'text-grey-50')}>{displayText || placeholder}</span>
            {triggerVariant === 'input' && (
              <div className="flex items-center gap-1 shrink-0">
                {displayText && allowClear ? (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={handleClear}
                    onKeyDown={handleClearKeyDown}
                    className="flex items-center justify-center w-4 h-4 rounded hover:bg-grey-10 transition-colors cursor-pointer focus-ring"
                    aria-label="Clear date"
                  >
                    <Icon icon="cross2" size={12} className="text-grey-50" />
                  </span>
                ) : (
                  <Icon icon="calendar" size={14} className="text-grey-60" />
                )}
              </div>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[258px] p-0" align="start" data-testid="date-picker-popover">
          <div className="flex flex-col w-full gap-1">
            {/* Header */}
            <div className={cn('p-2 pb-1 gap-2.5 flex flex-col w-full', !displayInput && !multiView && 'pt-0')}>
              {displayInput && (
                <Input
                  readOnly
                  value={formatDisplayText(tempSelection) || ''}
                  placeholder={placeholder}
                  className="text-xs select-none"
                />
              )}
              {multiView && views.length > 1 && (
                <RadioBar
                  value={internalView}
                  onValueChange={(value) => {
                    setInternalView(value as DateView);
                    setRangeStart(null);
                  }}
                  className="w-full max-w-full rounded-sm"
                >
                  {views.map((view) => (
                    <RadioBarItem key={view} value={view} className="flex-1 flex items-center justify-center">
                      <Text className="text-xs">{view}</Text>
                    </RadioBarItem>
                  ))}
                </RadioBar>
              )}
            </div>
            {/* Content Area */}
            {multiView ? (
              <>
                {internalView === 'Day' && (
                  <DayView
                    value={tempSelection}
                    onSelect={(date) => handleDateSelect(date, 'Day')}
                    rangeStart={rangeStart}
                    allowRange={allowRange}
                    minDate={minDate}
                    yearRange={yearRange}
                  />
                )}
                {internalView === 'Month' && (
                  <MonthView
                    value={tempSelection}
                    onSelect={(date) => handleDateSelect(date, 'Month')}
                    rangeStart={rangeStart}
                    allowRange={allowRange}
                  />
                )}
                {internalView === 'Quarter' && (
                  <QuarterView
                    value={tempSelection}
                    onSelect={(date) => handleDateSelect(date, 'Quarter')}
                    rangeStart={rangeStart}
                    allowRange={allowRange}
                  />
                )}
                {internalView === 'Year' && (
                  <YearView
                    value={tempSelection}
                    onSelect={(date) => handleDateSelect(date, 'Year')}
                    rangeStart={rangeStart}
                    allowRange={allowRange}
                    yearRange={yearRange}
                  />
                )}
              </>
            ) : (
              <DayView
                value={tempSelection}
                onSelect={(date) => handleDateSelect(date, 'Day')}
                rangeStart={rangeStart}
                allowRange={allowRange}
                minDate={minDate}
                yearRange={yearRange}
              />
            )}

            {/* Footer Buttons — hidden for instant-select single day pickers */}
            {!(instantSelect && !allowRange) && (
              <div className="flex items-center justify-end gap-2 p-2 pt-1">
                <Button variant="secondary" size="standard" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button variant="primary" size="standard" onClick={handleApply} disabled={!tempSelection}>
                  Apply
                </Button>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export { DatePicker };
export type { DatePickerProps };
